/**
 * PTE Vocabulary Builder — /pte/vocab
 *
 * Category-driven vocab library with 5 practice modes (choose_meaning,
 * complete_sentence, best_collocation, match_synonym, choose_in_context)
 * and progress panel (words practised, accuracy, streak, needs review).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { pteApi } from '@/lib/pteApi';
import { BookOpen, Sparkles, ChevronRight, CheckCircle2, XCircle, Loader2, RotateCcw, Trophy, Zap } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const MODES = [
  { key: 'choose_meaning', label: 'Choose the Meaning' },
  { key: 'complete_sentence', label: 'Complete the Sentence' },
  { key: 'best_collocation', label: 'Best Collocation' },
  { key: 'match_synonym', label: 'Match the Synonym' },
  { key: 'choose_in_context', label: 'Choose in Context' },
];

export default function PTEVocabPage() {
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState('');
  const [words, setWords] = useState([]);
  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState('choose_meaning');
  const [item, setItem] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [result, setResult] = useState(null);
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(false);



  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cRes, wRes] = await Promise.all([
          ax.get('/api/pte/vocab/categories'),
          ax.get('/api/pte/vocab/list', { params: { limit: 60 } }),
        ]);
        if (!alive) return;
        setCats(cRes.data.items || []);
        setWords(wRes.data.items || []);
        setTotal(wRes.data.total || 0);
        try {
          const p = await pteApi.vocabProgress();
          if (alive) setProg(p);
        } catch (_) {}
      } catch (_) { /* silent — banner shown by empty state */ }
    })();
    return () => { alive = false; };
  }, []);

  const loadWords = useCallback(async (nextCat) => {
    setCat(nextCat);
    const r = await ax.get(`/api/pte/vocab/list?limit=60&category=${nextCat}`);
    setWords(r.data.items || []); setTotal(r.data.total || 0);
  }, []);

  const nextItem = useCallback(async (nextMode) => {
    setLoading(true); setChosen(null); setResult(null);
    try {
      const r = await ax.post('/api/pte/vocab/practice/next', { category: cat, mode: nextMode || mode });
      setItem(r.data.item);
    } catch (_) { setItem(null); }
    finally { setLoading(false); }
  }, [cat, mode]);

  const submit = async () => {
    if (!chosen || !item) return;
    const correct = chosen === item.correct_key;
    setResult({ correct });
    try {
      await ax.post('/api/pte/vocab/practice/submit', {
        word_id: item.word_id, mode: item.mode, correct,
      });
      const p = await ax.get('/api/pte/vocab/progress').catch(() => ({ data: null }));
      setProg(p.data);
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pte-vocab-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Link to="/resources/pte-academic" className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <ChevronRight className="h-3 w-3 rotate-180" /> PTE Academic
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-1">Vocabulary Builder</h1>
            <p className="text-slate-600 text-sm md:text-base mt-1 max-w-2xl">Curated academic vocabulary, PTE collocations and transition language, with quick practice to earn XP.</p>
          </div>
          {prog && (
            <div className="flex gap-2">
              <div className="rounded-xl bg-white border border-slate-200 px-4 py-3 text-center min-w-[110px]" data-testid="vocab-practised">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Practised</div>
                <div className="text-2xl font-bold">{prog.practised || 0}</div>
              </div>
              <div className="rounded-xl bg-white border border-slate-200 px-4 py-3 text-center min-w-[110px]" data-testid="vocab-accuracy">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Accuracy</div>
                <div className="text-2xl font-bold">{prog.attempts ? Math.round(prog.accuracy * 100) : 0}%</div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white px-4 py-3 text-center min-w-[110px]" data-testid="vocab-streak">
                <div className="text-[10px] uppercase tracking-widest text-amber-100 inline-flex items-center justify-center gap-1"><Zap className="h-3 w-3" />Streak</div>
                <div className="text-2xl font-bold">{prog.streak || 0}d</div>
              </div>
            </div>
          )}
        </div>

        {/* Category strip */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => loadWords('')} data-testid="vocab-cat-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${!cat ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
            All ({total || '—'})
          </button>
          {cats.map((c) => (
            <button key={c.key} onClick={() => loadWords(c.key)} data-testid={`vocab-cat-${c.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${cat === c.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {/* Word list */}
          <div className="md:col-span-2 rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 inline-flex items-center gap-2">
              <BookOpen className="h-3.5 w-3.5" /> Words {cat && <span className="text-slate-400">· {cat}</span>}
            </div>
            <div className="divide-y divide-slate-100" data-testid="vocab-word-list">
              {words.slice(0, 20).map((w) => (
                <div key={w.id} className="py-3 flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">{w.word}</div>
                    <div className="text-[13px] text-slate-600">{w.meaning}</div>
                    {w.example && <div className="text-[12px] italic text-slate-500 mt-1">"{w.example}"</div>}
                    <div className="mt-1 text-[11px] text-slate-400 flex gap-2 flex-wrap">
                      {(w.synonyms || []).slice(0, 3).map((s) => (
                        <span key={s} className="px-1.5 py-0.5 rounded bg-slate-100">≈ {s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {words.length === 0 && (
                <div className="py-6 text-sm text-slate-500 text-center">No words in this category yet.</div>
              )}
            </div>
          </div>

          {/* Practice panel */}
          <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="vocab-practice-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> Practice
              </div>
              <select value={mode} onChange={(e) => { setMode(e.target.value); setItem(null); setResult(null); }}
                data-testid="vocab-mode-select"
                className="text-xs px-2 py-1 rounded border border-slate-200">
                {MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            {!item && (
              <button onClick={() => nextItem()} disabled={loading}
                data-testid="vocab-start-practice"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-slate-800 transition disabled:bg-slate-400">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {loading ? 'Loading…' : 'Start practice'}
              </button>
            )}
            {item && (
              <div>
                <div className="text-xs text-slate-500 mb-2 uppercase tracking-wider">{item.mode.replaceAll('_', ' ')}</div>
                <div className="text-slate-800 text-sm mb-4 whitespace-pre-line">{item.prompt}</div>
                <div className="space-y-2">
                  {(item.options || []).map((o) => {
                    const isChosen = chosen === o.key;
                    const isCorrect = result && o.key === item.correct_key;
                    const isWrongChoice = result && result.correct === false && isChosen;
                    return (
                      <button key={o.key} onClick={() => !result && setChosen(o.key)}
                        data-testid={`vocab-option-${o.key}`}
                        disabled={!!result}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                          isCorrect ? 'bg-emerald-50 border-emerald-400 text-emerald-900' :
                          isWrongChoice ? 'bg-red-50 border-red-400 text-red-900' :
                          isChosen ? 'bg-slate-900 border-slate-900 text-white' :
                          'bg-white border-slate-200 hover:border-slate-400 text-slate-800'
                        }`}>
                        <span className="font-bold mr-2">{o.key}.</span>{o.text}
                      </button>
                    );
                  })}
                </div>
                {!result ? (
                  <button onClick={submit} disabled={!chosen}
                    data-testid="vocab-submit"
                    className="mt-4 w-full px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:bg-slate-300">
                    Submit
                  </button>
                ) : (
                  <div className="mt-4">
                    <div className={`text-sm font-semibold inline-flex items-center gap-2 ${result.correct ? 'text-emerald-700' : 'text-red-700'}`}
                      data-testid="vocab-result">
                      {result.correct ? <><CheckCircle2 className="h-4 w-4" />Correct · +1 XP</> : <><XCircle className="h-4 w-4" />Not quite — the right answer is highlighted.</>}
                    </div>
                    <div className="text-xs text-slate-500 mt-2"><strong>{item.word}</strong> — {item.meaning}</div>
                    <button onClick={() => nextItem()} data-testid="vocab-next"
                      className="mt-3 w-full px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-slate-800 transition">
                      <RotateCcw className="h-4 w-4" /> Next word
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Needs review */}
        {prog && prog.needs_review?.length > 0 && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="vocab-needs-review">
            <div className="text-xs uppercase tracking-widest text-amber-800 font-bold mb-3 inline-flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5" /> Needs review
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {prog.needs_review.map((w) => (
                <div key={w.word_id} className="rounded-lg bg-white border border-amber-100 p-3">
                  <div className="text-sm font-semibold">{w.word}</div>
                  <div className="text-xs text-slate-600">{w.meaning}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{w.correct}/{w.attempts} correct</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
